import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Row, Col, Card, Statistic, Alert, Table, Typography, Tag, Divider } from 'antd';
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    FileTextOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { ImportTrainingProjectExamplesResult, ImportValidationError } from '../../types';
import './ImportReportDialog.less';

const { Title, Text } = Typography;

interface Props {
    /** Import result data to display */
    importResult: ImportTrainingProjectExamplesResult | null;
    /** Whether the dialog is visible */
    visible: boolean;
    /** Callback when dialog is closed */
    onClose: () => void;
}

/**
 * Dialog component that displays a comprehensive report of training project examples import results.
 * Shows statistics, breakdown of imported/skipped/failed examples, and detailed validation errors.
 */
const ImportReportDialog: React.FC<Props> = ({ importResult, visible, onClose }) => {
    // Don't render if no import result data
    if (!importResult) {
        return null;
    }

    const { totalCount, importedCount, skippedCount, duplicateCount, validationFailureCount, validationErrors } =
        importResult;

    // Calculate percentages for visual indicators
    const importedPercentage = totalCount > 0 ? Math.round((importedCount / totalCount) * 100) : 0;

    // Determine overall status for styling and messaging
    const isFullSuccess = importedCount === totalCount && validationErrors.length === 0;
    const isPartialSuccess = importedCount > 0 && skippedCount > 0;
    const isFailure = importedCount === 0 && totalCount > 0;

    // Get appropriate status info
    const getStatusInfo = () => {
        if (isFailure) {
            return {
                type: 'error' as const,
                icon: <CloseCircleOutlined />,
                title: 'Import Failed',
                message: `No examples were imported out of ${totalCount} total rows.`
            };
        } else if (isPartialSuccess) {
            return {
                type: 'warning' as const,
                icon: <ExclamationCircleOutlined />,
                title: 'Import Partially Successful',
                message: `${importedCount} out of ${totalCount} examples were imported successfully.`
            };
        } else if (isFullSuccess) {
            return {
                type: 'success' as const,
                icon: <CheckCircleOutlined />,
                title: 'Import Successful',
                message: `All ${totalCount} examples were imported successfully.`
            };
        } else {
            return {
                type: 'info' as const,
                icon: <InfoCircleOutlined />,
                title: 'Import Complete',
                message: `Import operation completed. ${importedCount} examples imported.`
            };
        }
    };

    const statusInfo = getStatusInfo();

    // Validation errors table columns
    const validationErrorColumns = [
        {
            title: 'Row #',
            dataIndex: 'rowNumber',
            key: 'rowNumber',
            width: 80,
            sorter: (a: ImportValidationError, b: ImportValidationError) => a.rowNumber - b.rowNumber
        },
        {
            title: 'Error Type',
            dataIndex: 'errorType',
            key: 'errorType',
            width: 150,
            render: (errorType: string) => <Tag color="red">{errorType}</Tag>
        },
        {
            title: 'Error Message',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            ellipsis: true
        },
        {
            title: 'Value Preview',
            dataIndex: 'valuePreview',
            key: 'valuePreview',
            width: 200,
            ellipsis: true,
            render: (value: string | undefined) =>
                value ? (
                    <Text code style={{ fontSize: '12px' }}>
                        {value}
                    </Text>
                ) : (
                    '-'
                )
        }
    ];

    return (
        <Modal
            className="import-report-dialog"
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined />
                    Import Report
                </div>
            }
            visible={visible}
            width={900}
            onCancel={onClose}
            maskClosable
            closable
            destroyOnClose
            centered
            footer={[
                <Button key="close" type="primary" onClick={onClose}>
                    Close
                </Button>
            ]}
        >
            <div className="import-report-content">
                {/* Status Alert */}
                <Alert
                    message={statusInfo.title}
                    description={statusInfo.message}
                    type={statusInfo.type}
                    icon={statusInfo.icon}
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                {/* Statistics Overview */}
                <Title level={5}>Import Statistics</Title>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Card>
                            <Statistic title="Total Processed" value={totalCount} prefix={<FileTextOutlined />} />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Successfully Imported"
                                value={importedCount}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<CheckCircleOutlined />}
                                suffix={`(${importedPercentage}%)`}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Duplicates Skipped"
                                value={duplicateCount}
                                valueStyle={{ color: '#faad14' }}
                                prefix={<ExclamationCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Validation Failures"
                                value={validationFailureCount}
                                valueStyle={{ color: '#ff4d4f' }}
                                prefix={<CloseCircleOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Detailed Breakdown */}
                {(duplicateCount > 0 || validationFailureCount > 0) && (
                    <>
                        <Title level={5}>Details</Title>
                        <div style={{ marginBottom: 24 }}>
                            {duplicateCount > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    <Text>
                                        <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                        {duplicateCount} duplicate examples were skipped (already exist in the training
                                        project)
                                    </Text>
                                </div>
                            )}
                            {validationFailureCount > 0 && (
                                <div>
                                    <Text>
                                        <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                        {validationFailureCount} examples failed validation and could not be imported
                                    </Text>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Validation Errors Table */}
                {validationErrors.length > 0 && (
                    <>
                        <Divider />
                        <Title level={5}>Validation Errors ({validationErrors.length})</Title>
                        <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
                            The following rows could not be imported due to validation errors:
                        </Text>
                        <Table
                            dataSource={validationErrors}
                            columns={validationErrorColumns}
                            size="small"
                            rowKey={record => `${record.rowNumber}-${record.errorType}`}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: false,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} validation errors`
                            }}
                            scroll={{ y: 300 }}
                        />
                    </>
                )}
            </div>
        </Modal>
    );
};

export default observer(ImportReportDialog);
